import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import api, { fileUrl } from "@/lib/api";
import { Search, Star, ShieldCheck, ChefHat } from "lucide-react";

export default function FindChef() {
  const [chefs, setChefs] = useState([]);
  const [q, setQ] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (specialty) params.specialty = specialty;
    if (verifiedOnly) params.verified_only = true;
    try {
      const { data } = await api.get("/chefs", { params });
      setChefs(data);
    } catch (err) {
      console.error("Chef search failed:", err);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-14">
        <div className="tick-label">Directory</div>
        <h1 className="font-display font-black text-5xl lg:text-6xl tracking-tighter mt-2">Find a chef.</h1>
        <p className="text-neutral-400 mt-3 max-w-xl">Discover licensed cooks and caterers by specialty. Perfect for events, popups, or private catering.</p>

        <div className="mt-8 grid md:grid-cols-4 gap-3">
          <div className="md:col-span-2 relative">
            <Search size={16} className="absolute left-3 top-4 text-neutral-500" />
            <input
              data-testid="chef-search-q"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Name or keyword"
              className="w-full border hairline pl-9 pr-4 py-3 focus:outline-none focus:border-[#FF3B30]"
            />
          </div>
          <input
            data-testid="chef-search-specialty"
            value={specialty}
            onChange={(e) => setSpecialty(e.target.value)}
            placeholder="Specialty (Pizza, Sushi…)"
            className="border hairline px-4 py-3 focus:outline-none focus:border-[#FF3B30]"
          />
          <label className="flex items-center gap-2 border hairline px-4 py-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="chef-verified-only"
              checked={verifiedOnly}
              onChange={(e) => setVerifiedOnly(e.target.checked)}
              className="accent-[#FF3B30] w-4 h-4"
            />
            <span className="tick-label">Licensed only</span>
          </label>
          <button data-testid="chef-search-submit" onClick={load} className="btn-signal md:col-span-4">Apply filters</button>
        </div>

        {loading ? (
          <div className="mt-14 tick-label">Loading...</div>
        ) : chefs.length === 0 ? (
          <div className="mt-14 text-center border hairline py-20">
            <div className="font-display text-3xl font-bold">No chefs match.</div>
            <div className="text-neutral-500 mt-3">Widen your filters or check back soon.</div>
          </div>
        ) : (
          <div data-testid="chef-grid" className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {chefs.map((c) => (
              <Link key={c.id} to={`/chef/${c.id}`} data-testid={`chef-card-${c.id}`} className="k-card block p-5">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 border hairline bg-neutral-900 overflow-hidden flex-shrink-0">
                    {c.avatar_url ? (
                      <img src={fileUrl(c.avatar_url)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><ChefHat size={22} className="text-neutral-700" /></div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <h3 className="font-display font-bold text-xl truncate">{c.name}</h3>
                      {c.license_verified && <ShieldCheck size={13} className="text-[#FF3B30] flex-shrink-0" />}
                    </div>
                    {c.review_count > 0 && (
                      <div className="mt-1 flex items-center gap-1 text-sm">
                        <Star size={12} className="fill-[#FF3B30] text-[#FF3B30]" />
                        <span className="font-mono">{c.rating.toFixed(1)}</span>
                        <span className="text-neutral-500">({c.review_count})</span>
                      </div>
                    )}
                  </div>
                </div>
                {c.bio && <p className="mt-3 text-sm text-neutral-400 line-clamp-2">{c.bio}</p>}
                {c.specialties?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {c.specialties.slice(0, 4).map((s, i) => (
                      <span key={i} className="text-xs border hairline px-2 py-0.5">{s}</span>
                    ))}
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
